/**
 * THE RESULTS CARD.
 *
 * The last thing a player reads, so it answers the two questions they actually
 * have — did I win, and was that a good one — before anything else. The rank
 * word does more work than the number: "Trophy Hunter" tells a first-timer
 * whether 18,400 was good, and the number alone never will.
 */

import type { Snapshot } from '../net/connection.js';
import { leaveSaloon, playAgain, setReady } from '../net/connection.js';
import { Chip } from './Bar.js';
import { Pip } from '../ui/Pip.js';

interface Props {
  snap: Snapshot;
  onOpenSettings: () => void;
}

export function Results({ snap, onOpenSettings }: Props) {
  const view = snap.view!;
  const results = view.results;
  const isHost = view.seats.find((s) => s.id === snap.seatId)?.isHost ?? false;

  if (!results) {
    return (
      <main className="room">
        <p className="muted">Tallying up…</p>
      </main>
    );
  }

  const mine = results.standings.find((s) => s.seatId === snap.seatId);
  const won = results.standings[0]?.seatId === snap.seatId && results.standings.length > 1;

  return (
    <main className="room">
      <div className="doors">
        <h1 className="marquee">{won ? 'Best gun in the house' : 'Hunt over'}</h1>
        {mine && (
          <p className="sub">
            {mine.rank} · {mine.score.toLocaleString()} points
          </p>
        )}
      </div>

      {results.records.length > 0 && (
        <div className="panel">
          <p className="tiny muted m-0">On the wall</p>
          <div className="list">
            {results.records.map((r, i) => (
              <div key={i} className="record">
                {r.kind === 'score'
                  ? `${r.handle} put ${r.value.toLocaleString()} on the house board`
                  : `${r.handle} hung a ${r.value}-pointer on the wall`}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="panel">
        <p className="tiny muted m-0">Standings</p>
        <div className="list">
          {results.standings.map((s, i) => (
            <div key={s.seatId} className={`standing${i === 0 ? ' first' : ''}`}>
              <span className="num">{i + 1}</span>
              <Chip hue={s.hue} />
              <div className="stack grow">
                <div className="who">{s.handle}</div>
                <div className="stat">
                  {s.rank} · {s.kills} taken · {s.bestRack ? `best ${s.bestRack} pt · ` : ''}
                  {s.accuracy}% on target
                </div>
              </div>
              <span className="score">{s.score.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="panel">
        {isHost ? (
          <button
            className="btn btn-primary btn-big btn-wide"
            onClick={() => {
              playAgain();
              // Keep the ante: whoever just played almost certainly wants
              // another round, and making eight people re-tap is friction with
              // nothing on the other side of it.
              setReady(true);
            }}
          >
            Another round
          </button>
        ) : (
          <p className="muted fs-md m-0">Waiting on the house to buy another round.</p>
        )}
        <button className="btn btn-wide mt-sm" onClick={leaveSaloon}>
          Out the door
        </button>
      </div>

      <div className="row between mt-md">
        <Pip status={snap.status} rtt={snap.rtt} />
        <button className="btn btn-ghost" onClick={onOpenSettings}>
          Settings
        </button>
      </div>
    </main>
  );
}
